import React from 'react';
import { css } from '@emotion/react';
import { Placement } from '@floating-ui/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Text, useTooltip } from '@frontend/design-system';
import { PrivateField } from './private-field';

type EditableFieldProps = {
  badge?: React.ReactNode;
  connector?: 'pipe' | 'none';
  displayComponent?: React.ReactNode;
  editComponent?: React.ReactNode;
  editMode?: boolean;
  isEdited?: boolean;
  isPrivate?: boolean;
  label: string;
  placement?: Placement;
  showTooltip?: boolean;
  subDisplayComponent?: React.ReactNode;
  subLabel?: React.ReactNode;
  tooltipText?: string;
};

type FieldLabelProps = {
  badge?: React.ReactNode;
  isEdited?: boolean;
  label: string;
};

const FieldLabel = ({ badge, isEdited, label }: FieldLabelProps) => {
  const { t } = useTranslation('analytics');
  return (
    <div css={styles.labelWrapper}>
      <Text color='subdued' css={styles.nowrap} size='medium'>
        {label}
      </Text>
      {badge}
      {isEdited && (
        <Text color='subdued' css={styles.italic} size='small'>
          {t('Edited')}
        </Text>
      )}
    </div>
  );
};

export const EditableField: React.FC<EditableFieldProps> = ({
  badge,
  connector = 'pipe',
  displayComponent,
  editComponent,
  editMode = false,
  label,
  isEdited = false,
  isPrivate = false,
  placement = 'top',
  showTooltip = false,
  subDisplayComponent,
  subLabel,
  tooltipText,
}) => {
  const { tooltipProps, triggerProps, Tooltip } = useTooltip({
    placement,
  });

  const useTwoRowLayout = !editMode && (!!subLabel || !!subDisplayComponent);

  if (isPrivate) {
    return (
      <div css={styles.fieldWrapper(false)}>
        <FieldLabel badge={badge} isEdited={false} label={label} />
        <div css={styles.displayWrapper(false)}>
          <PrivateField />
        </div>
      </div>
    );
  }

  if (editMode) {
    return (
      <div css={styles.fieldWrapper(false)}>
        <FieldLabel badge={badge} isEdited={isEdited} label={label} />
        <div>{editComponent}</div>
      </div>
    );
  }

  if (useTwoRowLayout) {
    return (
      <div css={styles.twoRowWrapper} data-testid={label}>
        <div css={styles.leftCol}>
          <FieldLabel badge={!useTwoRowLayout && badge} isEdited={isEdited} label={label} />

          <div css={styles.subLabelRow}>
            {connector === 'pipe' && <span css={styles.pipeBelow} />}
            {subLabel && (
              <div className='content-wrapper'>
                <Text color='subdued' size='small' css={styles.italic}>
                  {subLabel}
                </Text>
                {badge}
              </div>
            )}
          </div>
        </div>

        <div css={styles.rightCol}>
          <div {...triggerProps} css={styles.displayWrapper(showTooltip)}>
            {displayComponent}
          </div>

          {subDisplayComponent}
        </div>

        {showTooltip && <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>}
      </div>
    );
  }

  return (
    <div css={styles.fieldWrapper(true)} data-testid={label}>
      <FieldLabel badge={badge} isEdited={isEdited} label={label} />
      <div {...triggerProps} css={styles.displayWrapper(showTooltip)}>
        {displayComponent}
      </div>
      {showTooltip && <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>}
    </div>
  );
};

const styles = {
  fieldWrapper: (editMode: boolean) => css`
    display: flex;
    justify-content: space-between;
    align-items: ${editMode ? 'flex-start' : 'center'};
    gap: ${theme.spacing(0.5)};

    @media screen and (max-width: ${breakpoints.small.min}px) {
      align-items: flex-start;
      flex-direction: column;
      gap: ${theme.spacing(1)};
    }
  `,
  labelWrapper: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,
  displayWrapper: (showTooltip: boolean) => css`
    cursor: ${showTooltip ? 'pointer' : 'default'};
    width: ${showTooltip ? theme.spacing(5) : 'auto'};
    text-align: right;
    @media screen and (max-width: ${breakpoints.small.min}px) {
      text-align: left;
    }
  `,
  nowrap: css`
    white-space: nowrap;
  `,
  italic: css`
    font-style: italic;
  `,
  twoRowWrapper: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spacing(0.5)};

    @media screen and (max-width: ${breakpoints.small.min}px) {
      align-items: flex-start;
      flex-direction: column;
      gap: ${theme.spacing(1)};
    }
  `,
  leftCol: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(0.5)};
  `,
  rightCol: css`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: ${theme.spacing(0.5)};

    @media screen and (max-width: ${breakpoints.small.min}px) {
      align-items: flex-start;
    }
  `,
  subLabelRow: css`
    position: relative;
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};

    .content-wrapper {
      display: flex;
      align-items: center;
      gap: ${theme.spacing(1)};
    }
  `,
  pipeBelow: css`
    position: relative;
    display: inline-block;
    width: ${theme.spacing(1.25)};
    height: ${theme.spacing(2.25)};
    border: 1px solid ${theme.colors.neutral20};
    border-radius: ${theme.borderRadius.small};
    border-top: none;
    border-right: none;
    border-bottom-right-radius: ${theme.spacing(0)};
    border-top-left-radius: ${theme.spacing(0)};
    top: ${theme.spacing(-1)};
    left: ${theme.spacing(0.25)};
  `,
};
